<template>

  <v-container fluid class="mt-0 pt-0 page page-order-info">

    <!-- (data loading spinner) -->
    <v-container class="d-flex flex-column align-center justify-center" v-if="isDataLoading">
      <v-progress-circular indeterminate :size="50" style="margin-bottom:30px" color="primary" />
    </v-container>

    <v-container fluid class="px-0" v-else-if="order">

      <!-- Breadcrumb & Actions -->
      <v-row class="mt-4 mb-10">
        <v-col cols="12" xs="12" sm="6" md="6" lg="8" class="d-flex align-center justify-start page-breadcrumb-new">
          <!-- (breadcrumb) -->
          <template v-if="isCredit">
            <v-icon small class="mr-2">mdi-credit-card</v-icon>
            <router-link :to="{ name: 'credit-list-issued' }">Credits</router-link>
            <div class="separator">/</div>
            <div class="active-item">Order {{ order.thirdparty_order_number }} <span class="ml-1">(Credit Memo)</span></div>
          </template>
          <template v-else>
            <v-icon small class="mr-2">mdi-package-variant-closed</v-icon>
            <router-link :to="{ name: 'order-list-placed' }">Orders</router-link>
            <div class="separator">/</div>
            <div class="active-item">Order {{ order.thirdparty_order_number }}</div>
          </template>
        </v-col>
        <v-col cols="12" xs="12" sm="6" md="6" lg="4" class="d-flex align-center justify-sm-end">
          <!-- (download invoice) -->
          <order-invoice-download v-if="allowDownload && order" :order="order" />
        </v-col>
      </v-row>

      <!-- Order Info Header -->
      <order-info-header :order="order" />

      <!-- Page Navigation -->
      <v-tabs fixed-tabs show-arrows class="mt-5 page-navigation" v-model="activeTab">
        <v-tab @click="$root.goTo('order-info-overview')">Summary</v-tab>
        <v-tab @click="$root.goTo('order-info-supporting-docs')">Supporting Docs</v-tab>
        <v-tab @click="$root.goTo('order-info-attachment-docs')">Attachment Docs</v-tab>
        <v-tab @click="$root.goTo('order-info-tracking-history')" v-if="!isCredit">Tracking History</v-tab>
        <v-tab @click="$root.goTo('order-info-route-info')" v-if="showRouteInfo">Route Info</v-tab>
        <v-tab @click="$root.goTo('order-info-messages')" v-if="!isCredit">
          <v-icon style="font-size:18px;" class="pr-3 pt-1">mdi-message-text-outline</v-icon>
          Messages
        </v-tab>
      </v-tabs>

      <!-- Page View -->
      <transition name="fade-flash" mode="out-in">
        <router-view :key="$route.params.orderId" />
      </transition>

    </v-container>

  </v-container>

</template>

<script>
import OrderInvoiceDownload from '@/components/orders/OrderInvoiceDownload'
import OrderInfoHeader from '@/components/orders/OrderInfoHeader'

export default {
  name: 'OrderInfoRootPage',

  components: {
    OrderInvoiceDownload,
    OrderInfoHeader
  },

  props: {
    orderId: {
      required: true
    }
  },

  data () {
    return {
      activeTab: 0,
      isDataLoading: false,
      dataLoadingError: null,
      order: null,
      route: null
    }
  },

  computed: {
    appName () {
      return this.$store.getters['app/platform']
    },
    isCredit () {
      return this.order?.is_credit
    },
    allowDownload () {
      return this.order.order_status !== 'BEING_SCHEDULED'
    },
    showRouteInfo () {
      return this.order.route_id && this.order.order_status !== 'BEING_SCHEDULED'
    }
  },

  watch: {
    // Reload order on order ID change
    orderId: function (updated) {
      this.loadData()
      this.setActiveTab()
    }
  },

  async beforeMount () {
    // Load the order data
    await this.loadData()

    // Set active tab
    this.setActiveTab()
  },

  beforeDestroy () {
    this.order = null
  },

  methods: {
    setActiveTab () {
      switch (this.$route.name) {
        case 'order-info-overview': this.activeTab = 0; break
        case 'order-info-supporting-docs': this.activeTab = 1; break
        case 'order-info-attachment-docs': this.activeTab = 2; break
        case 'order-info-tracking-history': this.activeTab = 3; break
        case 'order-info-route-info': this.activeTab = 4; break
        case 'order-info-messages': this.activeTab = (this.showRouteInfo) ? 5 : 4; break
        default: this.activeTab = 0
      }
    },

    // Reload order data
    async reloadOrderData () {
      this.isDataLoading = true

      try {
        // Load order
        this.order = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.orderId}`,
          opts: {
            params: { with: 'order_details,shipper,receiver,submitter,dispatcher' }
          }
        })
      } catch (err) {
        console.log('Error reloading order data =>', err)
        this.dataLoadingError = err
      } finally {
        this.isDataLoading = false
      }
    },

    // Load order and route data
    async loadData () {
      this.isDataLoading = true

      try {
        // Load order
        this.order = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: `order/${this.orderId}`,
          opts: {
            params: { with: 'order_details,shipper,receiver,submitter,dispatcher' }
          }
        })

        // Load route, if scheduled
        if (this.order.route_id) {
          this.route = await this.$store.dispatch('sh/dataApi', {
            method: 'get',
            uri: `route/${this.order.route_id}`,
            opts: {
              params: { with: 'vehicle,driver,orders' }
            }
          })
        }
      } catch (err) {
        console.log('Error loading order and route data =>', err)
        this.dataLoadingError = err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
</style>
